/**
 * attackCycleStateMachine — Single Responsibility: Administrar el ciclo de ataque básico de entidades.
 *
 * Maneja windup (Pre-Windup, Windup, Post-Windup/Kiting, Cooldown), rangos de ataque,
 * comandos de ataque y la cancelación por movimiento.
 *
 * Contrato público:
 *   isInAttackRange(entity, targetEntity)
 *   issueAttackCommand(entity, enemyEntity)
 *   cancelAttackWithMovement(entity, newTargetX, newTargetZ)
 *   updateAttackCycle(entity, dt)
 */

export function isInAttackRange(entity, targetEntity) {
  if (!targetEntity || targetEntity.isDead) return false;
  const dx = targetEntity.x - entity.x;
  const dz = targetEntity.z - entity.z;
  const dist = Math.hypot(dx, dz);
  const range3D = entity.attackRange / 100;
  const totalAllowedDist = range3D + (targetEntity.radius / 16);
  return dist <= totalAllowedDist;
}

export function issueAttackCommand(entity, enemyEntity) {
  if (entity.isStunned) return false;
  if (enemyEntity && !enemyEntity.isDead && enemyEntity !== entity && enemyEntity.team !== entity.team) {
    entity.attackTarget = enemyEntity;
    entity.selectedTarget = enemyEntity;
    entity.lastCancelEvent = null;
    entity.cancelEventTimer = 0;
    return true;
  }
  entity.attackTarget = null;
  return false;
}

export function cancelAttackWithMovement(entity, newTargetX, newTargetZ) {
  if (entity.isStunned) return;

  const attackCycleDuration = 1.0 / (entity.as || 0.625);
  const windupTime = attackCycleDuration * (entity.windupPercent || 0.219);

  if (entity.isAttacking) {
    if (entity.windupTimer > 0 && entity.windupTimer < windupTime && !entity.hasFiredCurrentAttack) {
      entity.lastCancelEvent = 'PRE_WINDUP_CANCEL';
      entity.cancelEventTimer = 1.5;
    } else if (entity.hasFiredCurrentAttack) {
      entity.lastCancelEvent = 'POST_WINDUP_KITING';
      entity.cancelEventTimer = 1.5;
    } else {
      entity.lastCancelEvent = null;
      entity.cancelEventTimer = 0;
    }
  }

  entity.attackTarget = null;
  entity.isAttacking = false;
  entity.windupTimer = 0;
  entity.hasFiredCurrentAttack = false;
  entity.targetX = newTargetX;
  entity.targetZ = newTargetZ;
}

export function updateAttackCycle(entity, dt) {
  if (entity.isDead || entity.isStunned) return { fired: false, target: null, isQFlurry: false };

  if (entity.cancelEventTimer > 0) {
    entity.cancelEventTimer -= dt;
    if (entity.cancelEventTimer <= 0) {
      entity.cancelEventTimer = 0;
      entity.lastCancelEvent = null;
    }
  }

  const attackCycleDuration = 1.0 / (entity.as || 0.625);
  const windupTime = attackCycleDuration * (entity.windupPercent || 0.219);

  if (entity.attackCooldownTimer > 0) {
    entity.attackCooldownTimer -= dt;
    if (entity.attackCooldownTimer <= 0) {
      entity.attackCooldownTimer = 0;
      entity.hasFiredCurrentAttack = false;
    }
  }

  if (entity.isAttacking && entity.attackTarget && !entity.attackTarget.isDead) {
    if (entity.attackCooldownTimer <= 0) {
      entity.windupTimer += dt;

      if (entity.windupTimer >= windupTime && !entity.hasFiredCurrentAttack) {
        entity.hasFiredCurrentAttack = true;
        entity.attackCooldownTimer = attackCycleDuration - windupTime;
        entity.windupTimer = 0;
        const target = entity.attackTarget;
        const isQFlurry = !!entity.qActive;
        const isCrit = entity.critChance > 0 && Math.random() < entity.critChance;
        return { fired: true, target, isQFlurry, isCrit };
      }
    }
  } else {
    entity.windupTimer = 0;
    if (entity.attackTarget && entity.attackTarget.isDead) {
      entity.attackTarget = null;
    }
  }

  return { fired: false, target: null, isQFlurry: false };
}
